/**
 * 이상한 방 - 3D 정수 그리드 순수 함수
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3절(좌표) · 4.1절(그리드 해상도)
 *
 * 이 모듈은 부수효과가 없는 순수 함수만 담는다. 렌더러도 서버 판정도 같은 함수를 쓰게 하기 위해서다 -
 * 좌표 변환이 두 벌 존재하는 순간 "화면에는 붙어 보이는데 서버는 한 칸 어긋났다고 판정하는" 버그가 생긴다.
 *
 * 설계 전제 세 가지:
 *  1. 좌표는 3D 정수 그리드 단일 체계다(3절). 부모-자식 관계가 없으므로 변환은 셀↔월드 한 단계뿐이다.
 *  2. y가 높이 축이다. Three.js 관례이며, "책상 위 머그컵 = 책상보다 y가 높은 셀"이라는 표현과도 맞는다.
 *  3. 그리드는 유한하다(3.1절). 유한하다는 사실 자체가 "방 밖 배출" 익스플로잇을 자동 차단한다.
 */

import { CELL_SIZE_METERS, type Cell3, type CatalogEntry, type RoomBounds, type Rot } from './types'

/**
 * 셀 중심 오프셋.
 * 셀 (0,0,0)이 차지하는 월드 구간은 [0, CELL_SIZE_METERS)이고, 렌더용 좌표는 그 '중심'이어야 한다.
 * 모서리를 반환하면 박스 메시가 반 칸씩 밀려 그리드와 어긋난다.
 */
const CELL_CENTER_OFFSET = 0.5

/**
 * footprint 각 축의 최소 셀 개수.
 * 8절: 최소 소품의 footprint가 1×1×1이라는 것이 그리드 해상도(4.1절)의 정의 근거다.
 * 따라서 0 이하의 축은 난이도 조절이 아니라 카탈로그 데이터 오류다.
 */
const MIN_FOOTPRINT_CELLS = 1

/** 90° 회전. 이 단계에서만 x/z 축이 교환된다. */
const ROT_QUARTER_TURN: Rot = 1

/** 270° 회전. 점유 영역의 크기만 보면 90°와 동일하다(축 정렬 박스이므로). */
const ROT_THREE_QUARTER_TURN: Rot = 3

/**
 * 카탈로그는 외부 데이터(JSON·에디터 입력)에서 들어오므로 경계에서 검증한다.
 * 검증 없이 통과시키면 footprint 0이 조용히 '빈 점유 목록'이 되어,
 * 그 소품만 셀 점유 규칙(3.1절)을 우회하는 은닉 익스플로잇이 열린다.
 */
function assertValidFootprint(footprint: Cell3): void {
  const isValid =
    Number.isInteger(footprint.x) &&
    footprint.x >= MIN_FOOTPRINT_CELLS &&
    Number.isInteger(footprint.y) &&
    footprint.y >= MIN_FOOTPRINT_CELLS &&
    Number.isInteger(footprint.z) &&
    footprint.z >= MIN_FOOTPRINT_CELLS

  if (isValid) return

  throw new RangeError(
    `footprint의 각 축은 ${MIN_FOOTPRINT_CELLS} 이상의 정수여야 한다: ` +
      `(${footprint.x}, ${footprint.y}, ${footprint.z})`,
  )
}

/**
 * 월드 좌표 유한성 검증.
 * 레이캐스트가 아무것도 맞히지 못했을 때 NaN이 흘러들어올 수 있는데,
 * Math.floor(NaN)은 NaN이라 그대로 두면 NaN 셀이 상태에 저장되어 diff 전체가 조용히 망가진다.
 */
function assertFiniteWorldCoord(axisName: string, value: number): void {
  if (Number.isFinite(value)) return
  throw new RangeError(`월드 좌표 ${axisName}가 유한수가 아니다: ${value}`)
}

/**
 * 셀 좌표를 Map/Set 키로 쓸 수 있는 문자열로 만든다.
 * 셀 점유 규칙(3.1절 "한 셀에는 오브젝트 하나")을 O(1)로 검사하기 위한 것이며,
 * 음수 좌표도 서로 구별된다.
 *
 * 저장·전송 포맷이 아니다 - 영속화가 필요하면 Cell3를 그대로 직렬화한다.
 */
export function cellKey(cell: Cell3): string {
  return `${cell.x},${cell.y},${cell.z}`
}

/**
 * 회전이 적용된 점유 크기를 돌려준다.
 *
 * rot 1·3(90°/270°)에서는 x/z 축이 교환된다. y는 높이 축이라 어떤 회전에서도 불변이다.
 * rot 0·2(0°/180°)는 축 정렬 박스 기준으로 크기가 같으므로 그대로 통과시킨다 -
 * 180° 회전은 겉모습을 바꾸지만 '차지하는 칸 수'는 바꾸지 않는다.
 */
export function rotatedFootprint(footprint: Cell3, rot: Rot): Cell3 {
  assertValidFootprint(footprint)

  const isAxisSwapped = rot === ROT_QUARTER_TURN || rot === ROT_THREE_QUARTER_TURN
  if (!isAxisSwapped) return { x: footprint.x, y: footprint.y, z: footprint.z }

  return { x: footprint.z, y: footprint.y, z: footprint.x }
}

/**
 * 오브젝트가 실제로 점유하는 셀 전부를 열거한다.
 *
 * `cell`은 점유 박스의 **최소 코너**(원점)로 해석한다. 즉 회전은 중심이 아니라 최소 코너를 기준으로 하며,
 * 점유 영역은 항상 +x/+y/+z 방향으로 자란다. 이 규약 덕분에 경계 검사가 뺄셈 없이 끝난다.
 *
 * 이 목록은 두 곳에서 그대로 쓰인다:
 *  - 셀 점유 규칙 검사(3.1절)
 *  - 제거된 오브젝트의 정답 영역(6.2절 "점유하던 셀 전부를 정답 영역으로. 여유 있게")
 *
 * 순서는 x → y → z 순으로 안정적이다(z가 가장 빠르게 증가).
 */
export function occupiedCells(cell: Cell3, entry: CatalogEntry, rot: Rot): readonly Cell3[] {
  const size = rotatedFootprint(entry.footprint, rot)
  const cells: Cell3[] = []

  for (let offsetX = 0; offsetX < size.x; offsetX += 1) {
    for (let offsetY = 0; offsetY < size.y; offsetY += 1) {
      for (let offsetZ = 0; offsetZ < size.z; offsetZ += 1) {
        cells.push({ x: cell.x + offsetX, y: cell.y + offsetY, z: cell.z + offsetZ })
      }
    }
  }

  return cells
}

/**
 * 점유 셀 **전부**가 방 안에 있는지 판정한다. 한 칸이라도 걸치면 false다.
 *
 * 유효 범위는 각 축 [0, size)다. 3.1절: 그리드가 유한하다는 것이 "방 밖 배출"을 막는 장치이므로
 * 여기서 관대해지면 규칙 자체가 무의미해진다.
 *
 * 점유 영역이 항상 축 정렬 박스이므로 최소 코너와 최대 코너만 보면 전 셀을 검사한 것과 같다 -
 * 큰 가구에서 수십 개 셀을 만들어 버리는 낭비를 피한다.
 */
export function isInBounds(
  cell: Cell3,
  entry: CatalogEntry,
  rot: Rot,
  bounds: RoomBounds,
): boolean {
  if (cell.x < 0 || cell.y < 0 || cell.z < 0) return false

  const size = rotatedFootprint(entry.footprint, rot)

  return (
    cell.x + size.x <= bounds.sizeX &&
    cell.y + size.y <= bounds.sizeY &&
    cell.z + size.z <= bounds.sizeZ
  )
}

/**
 * 셀 좌표 → 셀 **중심**의 월드 좌표(미터). 렌더 레이어 전용 변환이다.
 *
 * 중심을 반환하는 이유: Three.js의 BoxGeometry는 원점이 박스 중심이라
 * 모서리 좌표를 주면 메시가 반 칸 밀린다.
 * CELL_SIZE_METERS가 2의 거듭제곱(0.25)이라 이 곱셈은 부동소수점 오차 없이 정확하다.
 */
export function cellToWorld(cell: Cell3): { x: number; y: number; z: number } {
  return {
    x: (cell.x + CELL_CENTER_OFFSET) * CELL_SIZE_METERS,
    y: (cell.y + CELL_CENTER_OFFSET) * CELL_SIZE_METERS,
    z: (cell.z + CELL_CENTER_OFFSET) * CELL_SIZE_METERS,
  }
}

/**
 * 월드 좌표(미터) → 그것을 담고 있는 셀. 포인터 레이캐스트 결과를 그리드로 되돌릴 때 쓴다.
 *
 * Math.floor 기반이라 셀 경계값은 항상 '큰 쪽' 셀에 속하고(0.25m → 셀 1),
 * 음수 구간에서도 절단이 아니라 내림이므로 -0.1m는 셀 -1이 된다.
 * 이 규약이 cellToWorld와 왕복 일관성(worldToCell(cellToWorld(c)) === c)을 보장한다.
 */
export function worldToCell(x: number, y: number, z: number): Cell3 {
  assertFiniteWorldCoord('x', x)
  assertFiniteWorldCoord('y', y)
  assertFiniteWorldCoord('z', z)

  return {
    x: Math.floor(x / CELL_SIZE_METERS),
    y: Math.floor(y / CELL_SIZE_METERS),
    z: Math.floor(z / CELL_SIZE_METERS),
  }
}
